<!-- eslint-disable -->
<template>
  <div class="quo-crm">
    <!-- Header -->
    <header class="quo-header shadow-sm">
      <div class="d-flex align-items-center">
        <div class="quo-header__icon mr-2">
          <feather-icon icon="FileTextIcon" size="24" />
        </div>
        <div>
          <h1 class="quo-header__title">Quotations</h1>
          <p class="quo-header__subtitle">
            Every quotation you have raised — build them from an opportunity, follow them to accepted or lost.
          </p>
        </div>
      </div>
      <button type="button" class="quo-btn quo-btn--ghost" @click="fetchQuotations">
        <feather-icon icon="RefreshCwIcon" size="15" /> <span>Refresh</span>
      </button>
    </header>

    <!-- Status summary: the pipeline of the document itself -->
    <section class="quo-stats">
      <button
        v-for="s in statCards"
        :key="s.key"
        type="button"
        class="quo-stat"
        :class="[`quo-stat--${s.variant}`, { 'quo-stat--active': statusFilter === s.key }]"
        @click="statusFilter = statusFilter === s.key ? '' : s.key"
      >
        <div class="quo-stat__value">{{ s.count }}</div>
        <div class="quo-stat__label">{{ s.label }}</div>
        <div v-if="s.total !== null" class="quo-stat__total">{{ money(s.total) }}</div>
      </button>
    </section>

    <!-- Toolbar -->
    <section class="quo-toolbar">
      <div class="quo-search">
        <feather-icon icon="SearchIcon" size="16" class="quo-search__icon" />
        <input
          v-model="search"
          type="text"
          class="quo-search__input"
          placeholder="Search quotation number, company, contact or request number…"
        />
      </div>
      <select v-model="statusFilter" class="quo-select ml-2">
        <option value="">All statuses</option>
        <option value="draft">Draft</option>
        <option value="sent">Sent</option>
        <option value="accepted">Accepted</option>
        <option value="rejected">Rejected</option>
        <option value="handed_over">Handed over</option>
      </select>
      <label v-if="access.canViewTeam" class="quo-mine ml-2">
        <input v-model="mineOnly" type="checkbox" @change="fetchQuotations" />
        <span>Only mine</span>
      </label>
    </section>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"><span class="sr-only">Loading…</span></div>
      <p class="mt-2 text-muted">Loading quotations…</p>
    </div>

    <div v-else-if="error" class="quo-empty">
      <feather-icon icon="AlertTriangleIcon" size="36" class="text-danger mb-1" />
      <h4>Could not load quotations</h4>
      <p class="text-muted">{{ error }}</p>
    </div>

    <!-- Honest empty state: say WHERE a quotation comes from -->
    <div v-else-if="!filtered.length" class="quo-empty">
      <feather-icon icon="FileTextIcon" size="42" class="text-muted mb-1" />
      <h4>{{ quotations.length ? 'Nothing matches these filters' : 'No quotations yet' }}</h4>
      <p class="text-muted" v-if="!quotations.length">
        A quotation is built from an opportunity: open a card on the pipeline and use its
        <strong>Quotation</strong> tab. It will then appear here.
      </p>
      <button v-else type="button" class="quo-btn quo-btn--ghost mt-1" @click="clearFilters">
        Clear filters
      </button>
    </div>

    <!-- Table -->
    <section v-else class="quo-table-wrap">
      <table class="quo-table">
        <thead>
          <tr>
            <th>Quotation</th>
            <th>Company / contact</th>
            <th>Scheme</th>
            <th>Request no.</th>
            <th class="text-right">Total</th>
            <th>Status</th>
            <th>Raised by</th>
            <th>Dates</th>
            <th class="text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="q in filtered" :key="q.id">
            <td>
              <div class="font-weight-bold">{{ q.quotation_code || ('#' + q.id) }}</div>
              <small class="text-muted">{{ (q.items && q.items.length) || 0 }} line{{ ((q.items && q.items.length) || 0) === 1 ? '' : 's' }}</small>
            </td>
            <td>
              <div>{{ q.company_name || '—' }}</div>
              <small class="text-muted">{{ q.contact_person || '' }}</small>
            </td>
            <td :title="q.scheme">{{ q.scheme || '—' }}</td>
            <td>
              <span v-if="q.request_number">{{ q.request_number }}</span>
              <!-- Required by the BRD before a quotation can be sent -->
              <span v-else class="text-warning" title="Required before this quotation can be marked as sent">missing</span>
            </td>
            <td class="text-right font-weight-bold">
              {{ q.currency || 'AED' }} {{ money(q.total) }}
              <!-- `discount` is a flat AMOUNT, not a percentage — see
                   CrmQuotation::recalcTotals(). Labelling it "%" was wrong. -->
              <div v-if="Number(q.discount) > 0" class="quo-discount">
                −{{ money(q.discount) }} disc.
              </div>
              <div v-if="Number(q.taxes) > 0" class="quo-tax">incl. VAT {{ money(q.taxes) }}</div>
            </td>
            <td>
              <span class="quo-status" :class="`quo-status--${q.status}`">{{ statusLabel(q.status) }}</span>
            </td>
            <td>{{ q.created_by_name || '—' }}</td>
            <td class="quo-dates">
              <div v-if="q.quotation_date"><small>raised {{ q.quotation_date }}</small></div>
              <div v-if="q.sent_at"><small>sent {{ shortDate(q.sent_at) }}</small></div>
              <div v-if="q.expiration"><small :class="{ 'text-danger': isExpired(q) }">expires {{ q.expiration }}</small></div>
            </td>
            <td class="text-right">
              <button
                v-if="q.card_id"
                type="button"
                class="quo-btn quo-btn--sm quo-btn--primary"
                title="Open the opportunity this quotation belongs to"
                @click="openCard(q)"
              >
                Open opportunity
              </button>
              <span v-else class="text-muted">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
/* eslint-disable */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

/**
 * MID-CRM — Quotations page.
 *
 * Quotations already existed in the backend and in the card drawer, but there was NO
 * way to see them as a list: a rep had to remember which opportunity a quotation was
 * on and open that card. "The quotation part cannot be found" (Karim, 12 Aug).
 *
 * Read-only on purpose: creating and editing stays on the opportunity, because a
 * quotation without its deal is not a thing this business has. This page answers
 * "what have we quoted, for how much, and what came back".
 */
export default {
  name: 'QuotationsCRM',
  data() {
    return {
      quotations: [],
      loading: false,
      error: null,
      search: '',
      statusFilter: '',
      mineOnly: false,
      access: { canViewTeam: false },
    }
  },
  computed: {
    filtered() {
      let r = [...this.quotations]
      if (this.statusFilter) r = r.filter(q => q.status === this.statusFilter)
      if (this.search) {
        const s = this.search.toLowerCase()
        r = r.filter(q =>
          (q.quotation_code && q.quotation_code.toLowerCase().includes(s))
          || (q.company_name && q.company_name.toLowerCase().includes(s))
          || (q.contact_person && q.contact_person.toLowerCase().includes(s))
          || (q.request_number && String(q.request_number).toLowerCase().includes(s))
          || (q.scheme && q.scheme.toLowerCase().includes(s)))
      }
      return r
    },
    statCards() {
      const by = st => this.quotations.filter(q => q.status === st)
      const sum = list => list.reduce((t, q) => t + Number(q.total || 0), 0)
      const mk = (key, label, variant) => {
        const list = by(key)
        return { key, label, variant, count: list.length, total: sum(list) }
      }
      return [
        { key: '', label: 'All', variant: 'primary', count: this.quotations.length, total: sum(this.quotations) },
        mk('draft', 'Draft', 'secondary'),
        mk('sent', 'Sent', 'info'),
        mk('accepted', 'Accepted', 'success'),
        mk('rejected', 'Rejected', 'danger'),
        mk('handed_over', 'Handed over', 'warning'),
      ]
    },
  },
  created() {
    this.fetchAccess()
    this.fetchQuotations()
  },
  methods: {
    async fetchAccess() {
      try {
        const res = await axios.get('crm/me', { timeout: 20000 })
        const body = decryptData(res.data.payload)
        if (body && body.data) this.access = body.data
      } catch (e) { /* stay with the safe default: own only */ }
    },

    async fetchQuotations() {
      this.loading = true
      this.error = null
      try {
        const params = {}
        if (this.mineOnly) params.mine = 1
        const res = await axios.get('crm/quotations', { params, timeout: 20000 })
        const body = decryptData(res.data.payload)
        this.quotations = (body && Array.isArray(body.data)) ? body.data : []
      } catch (e) {
        this.quotations = []
        this.error = 'The server did not return the quotation list. Please retry.'
      } finally {
        this.loading = false
      }
    },

    /** Quotations live on an opportunity — deep-link straight to that card. */
    openCard(q) {
      this.$router.push({
        name: 'crm-system-pipeline',
        query: { card: q.card_id, pipeline: q.card_id && q.card_id.startsWith('R-') ? 'renewals' : 'deals' },
      })
    },

    clearFilters() {
      this.search = ''
      this.statusFilter = ''
    },

    statusLabel(s) {
      return ({
        draft: 'Draft', sent: 'Sent', accepted: 'Accepted',
        rejected: 'Rejected', handed_over: 'Handed over',
      })[s] || (s || '—')
    },

    isExpired(q) {
      if (!q.expiration) return false
      return q.expiration < new Date().toISOString().split('T')[0]
        && !['accepted', 'handed_over'].includes(q.status)
    },

    money(v) {
      return Math.round(Number(v || 0)).toLocaleString('en-US')
    },

    shortDate(v) {
      if (!v) return ''
      return String(v).split('T')[0]
    },
  },
}
</script>

<style lang="scss" scoped>
.quo-crm { padding: 1.25rem 1.5rem; }

.quo-header {
  display: flex; align-items: center; justify-content: space-between;
  background: linear-gradient(135deg, #7367f0, #9e95f5);
  color: #fff; border-radius: .6rem; padding: 1.1rem 1.3rem; margin-bottom: 1rem;
}
.quo-header__icon { background: rgba(255,255,255,.2); border-radius: 50%; padding: .5rem; display: flex; }
.quo-header__title { font-size: 1.35rem; font-weight: 700; margin: 0; color: #fff; }
.quo-header__subtitle { margin: 0; opacity: .9; font-size: .85rem; }

.quo-stats { display: flex; gap: .75rem; flex-wrap: wrap; margin-bottom: 1rem; }
.quo-stat {
  flex: 1 1 140px; text-align: left; border: 1px solid #e8eaf0; background: #fff;
  border-radius: .6rem; padding: .7rem .9rem; cursor: pointer; transition: all .15s ease;
  &:hover { border-color: #7367f0; }
  &--active { border-color: #7367f0; box-shadow: 0 0 0 2px rgba(115,103,240,.15); }
}
.quo-stat__value { font-size: 1.35rem; font-weight: 800; color: #2f2f4a; }
.quo-stat__label { font-size: .75rem; font-weight: 700; color: #8a8aa3; text-transform: uppercase; letter-spacing: .03em; }
.quo-stat__total { font-size: .72rem; color: #b3b3c6; font-weight: 600; }

.quo-toolbar { display: flex; align-items: center; margin-bottom: .9rem; flex-wrap: wrap; gap: .4rem; }
.quo-search { position: relative; flex: 1 1 320px; }
.quo-search__icon { position: absolute; left: .6rem; top: 50%; transform: translateY(-50%); color: #b3b3c6; }
.quo-search__input {
  width: 100%; padding: .45rem .6rem .45rem 2rem; border: 1px solid #e8eaf0;
  border-radius: .4rem; font-size: .85rem;
}
.quo-select { padding: .45rem .6rem; border: 1px solid #e8eaf0; border-radius: .4rem; font-size: .85rem; background: #fff; }
.quo-mine { display: flex; align-items: center; gap: .35rem; font-size: .82rem; color: #6e6b7b; margin: 0; }

.quo-table-wrap { background: #fff; border: 1px solid #e8eaf0; border-radius: .6rem; overflow-x: auto; }
.quo-table { width: 100%; border-collapse: collapse; font-size: .84rem; }
.quo-table th {
  text-align: left; padding: .6rem .75rem; background: #f8f8fb; color: #6e6b7b;
  font-weight: 700; font-size: .72rem; text-transform: uppercase; letter-spacing: .03em;
  border-bottom: 1px solid #e8eaf0; white-space: nowrap;
}
.quo-table td { padding: .6rem .75rem; border-bottom: 1px solid #f1f1f6; vertical-align: top; }
.quo-table tbody tr:hover { background: #fafaff; }

.quo-status {
  display: inline-block; padding: .12rem .5rem; border-radius: 10px;
  font-size: .7rem; font-weight: 800; text-transform: uppercase; letter-spacing: .03em;
  background: #eee; color: #6e6b7b;
  &--draft { background: rgba(110,107,123,.12); color: #6e6b7b; }
  &--sent { background: rgba(0,207,232,.15); color: #00a3ba; }
  &--accepted { background: rgba(40,199,111,.15); color: #1f9d5b; }
  &--rejected { background: rgba(234,84,85,.15); color: #d3474a; }
  &--handed_over { background: rgba(255,159,67,.18); color: #b26a1b; }
}
.quo-discount { font-size: .7rem; color: #b26a1b; font-weight: 700; }
.quo-tax { font-size: .68rem; color: #8a8aa3; font-weight: 600; }
.quo-dates small { display: block; color: #8a8aa3; }

.quo-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 3rem 1rem; background: #fff; border: 1px dashed #e8eaf0; border-radius: .6rem; text-align: center;
}

.quo-btn {
  display: inline-flex; align-items: center; gap: .35rem; border-radius: .4rem;
  padding: .4rem .75rem; font-size: .82rem; font-weight: 600; cursor: pointer; border: 1px solid transparent;
  &--ghost { background: rgba(255,255,255,.18); color: #fff; border-color: rgba(255,255,255,.35); }
  &--primary { background: #7367f0; color: #fff; }
  &--sm { padding: .25rem .6rem; font-size: .76rem; }
}
.quo-empty .quo-btn--ghost { background: #fff; color: #6e6b7b; border-color: #e8eaf0; }
</style>
